/**
 * Returns the 1-based sort priority index of `column` in the sort array, or null if not present.
 */
export function getSortIndex(sortingColumns, column) {
    const idx = sortingColumns.findIndex(entry => matchesColumn(entry.sortingColumn, column));
    return idx === -1 ? null : idx + 1;
}
/**
 * Replace the entire sort state with a single column.
 */
export function replaceSort(column, isDescending) {
    return [{ sortingColumn: column, isDescending }];
}
/**
 * Append a column at the end of the current sort.
 */
export function appendSort(current, column, isDescending) {
    return [...current, { sortingColumn: column, isDescending }];
}
/**
 * Toggle the direction of a column already in the sort, keeping its position.
 */
export function toggleDirection(current, column) {
    return current.map(entry => matchesColumn(entry.sortingColumn, column) ? { ...entry, isDescending: !entry.isDescending } : entry);
}
/**
 * Set the direction of a column already in the sort to a specific value, keeping its position.
 */
export function setDirection(current, column, isDescending) {
    return current.map(entry => (matchesColumn(entry.sortingColumn, column) ? { ...entry, isDescending } : entry));
}
/**
 * Remove a column from the sort and reindex remaining priorities.
 */
export function removeSort(current, column) {
    return current.filter(entry => !matchesColumn(entry.sortingColumn, column));
}
function matchesColumn(sortingColumn, column) {
    return (column === sortingColumn ||
        (column.sortingField !== undefined && column.sortingField === sortingColumn.sortingField) ||
        (column.sortingComparator !== undefined && column.sortingComparator === sortingColumn.sortingComparator));
}
/**
 * Best-effort text label for a sorted column. `column.header` is a ReactNode, so we can only use it
 * directly when it is a string; otherwise we fall back to the sorting field or column id.
 */
function getColumnLabel(column, sortingColumn) {
    var _a, _b;
    if (column && typeof column.header === 'string') {
        return column.header;
    }
    return (_a = sortingColumn.sortingField) !== null && _a !== void 0 ? _a : String((_b = column === null || column === void 0 ? void 0 : column.id) !== null && _b !== void 0 ? _b : '');
}
/**
 * Builds a screen-reader announcement for the current multi-column sort state by joining per-column
 * fragments in code (charts-style). The fragment renderers are resolved from i18n by the caller;
 * returns an empty string when the relevant renderers are unavailable so no announcement is made.
 */
export function buildSortLiveAnnouncement({ sortingColumns = [], columnDefinitions, renderSortColumn, renderSortOrder, sortCleared, formatList, resolveColumnLabel, }) {
    if (sortingColumns.length === 0) {
        return sortCleared !== null && sortCleared !== void 0 ? sortCleared : '';
    }
    if (!renderSortColumn || !renderSortOrder) {
        return '';
    }
    const parts = sortingColumns.map(({ sortingColumn, isDescending }) => {
        const column = columnDefinitions.find(candidate => matchesColumn(sortingColumn, candidate));
        // Prefer the rendered (localized) header text supplied by the caller; fall back to a best-effort
        // label derived from the column definition when it is unavailable (e.g. no id, or server-side render).
        const columnLabel = (resolveColumnLabel === null || resolveColumnLabel === void 0 ? void 0 : resolveColumnLabel(column)) || getColumnLabel(column, sortingColumn);
        return renderSortColumn({ columnLabel, isDescending: !!isDescending });
    });
    const columns = formatList ? formatList(parts) : parts.join(', ');
    return renderSortOrder({ columns });
}
//# sourceMappingURL=utils.js.map